'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { RATING_REVIEWS_MENU_ID } from '@headless-workspace/config';
import { useModalActions } from '@headless-workspace/core-ui';
import { ConnectedUserInfo, RatingAndReviewsFormValue } from '@headless-workspace/domain/common/server';
import { GlowImage, GlowModal, GlowTextBody, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component, ImageWithSrc } from '@headless-workspace/typings';
import { ReviewPayload } from '../../../../server';
import { RatingsAndReviewsForm } from './RatingsAndReviewsForm';
import { RatingsAndReviewsFormInformation } from './RatingsAndReviewsFormInformation';

type RatingsAndReviewsModalProps = PropsWithLocale & {
    productId: string;
    userInfo: ConnectedUserInfo;
    ratingAndReviewsForm: RatingAndReviewsFormValue;
};

const IMG_WIDTH = 96;
const IMG_HEIGHT = 96;

const ProductInfo = ({
    name,
    brand,
    image,
    isSubmitted,
    title,
}: {
    name: string;
    brand: string;
    image?: ImageWithSrc;
    isSubmitted: boolean;
    title: string;
}) => (
    <div className={'flex flex-col gap-1'}>
        {isSubmitted && <GlowTextBody fontWeight={'bold'} text={title} TagName={'h4'} />}
        <div className={'flex flex-row gap-1 items-center'}>
            {image && (
                <GlowImage
                    className={
                        'outline outline-medium outline-solid rounded-0.5 outline-border-primary w-full h-full max-w-[6rem] max-h-[6rem]'
                    }
                    image={image}
                    width={IMG_WIDTH}
                    height={IMG_HEIGHT}
                />
            )}
            <div className={'flex flex-col'}>
                <GlowTextBody text={name} />
                <GlowTextBody text={brand} fontWeight={'bold'} />
            </div>
        </div>
    </div>
);

export const RatingsAndReviewsModal: Component<RatingsAndReviewsModalProps> = ({
    productId,
    userInfo,
    locale,
    ratingAndReviewsForm,
}) => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [reviewPayload, setReviewPayload] = useState<ReviewPayload | undefined>();
    const t = useTranslations('Discover.Pdp.ratingAndReviewsForm');
    const { close } = useModalActions();
    const userCard = userInfo?.loyalty?.sephoraCard;

    const closeRatingsAndReviewsModal = useCallback(() => close(RATING_REVIEWS_MENU_ID), [close]);

    const handlePostSubmit = useCallback(() => {
        closeRatingsAndReviewsModal();
        setIsSubmitted(false);
        setReviewPayload(undefined);
    }, [closeRatingsAndReviewsModal]);

    useEffect(() => {
        setIsSubmitted(false);
    }, [productId]);

    const handleSubmitForm = useCallback((isSuccess: boolean, data: ReviewPayload) => {
        setIsSubmitted(isSuccess);
        setReviewPayload(data);
    }, []);

    const { product, fields } = ratingAndReviewsForm;

    const ProductInfosContent = useMemo(
        () => (
            <ProductInfo
                isSubmitted={isSubmitted}
                name={product.name}
                brand={product.brand.name}
                image={product.image}
                title={t('label.overviewTitle')}
            />
        ),
        [isSubmitted, product, t],
    );

    const age = useMemo(() => {
        return fields.age.options.find((option) => option.value === reviewPayload?.age)?.label;
    }, [fields, reviewPayload]);

    const usageDuration = useMemo(() => {
        return fields.usageDuration.options.find((option) => option.value === reviewPayload?.usageDuration)?.label;
    }, [fields, reviewPayload]);

    return (
        <GlowModal.Container
            title={t('label.modalTitle')}
            onClick={closeRatingsAndReviewsModal}
            onClickBackCta={isSubmitted ? () => setIsSubmitted(false) : undefined}
        >
            {!isSubmitted ? (
                <RatingsAndReviewsForm
                    formInfo={fields}
                    onSubmit={handleSubmitForm}
                    locale={locale}
                    userInfo={userInfo}
                    reviewPayload={reviewPayload}
                    ProductInfos={ProductInfosContent}
                />
            ) : (
                <RatingsAndReviewsFormInformation
                    gender={userInfo.gender}
                    locale={locale}
                    userCard={userCard}
                    productId={productId}
                    reviewPayload={reviewPayload}
                    acceptedTermsAndConditions={fields.acceptedTermsAndConditions}
                    age={age}
                    usageDuration={usageDuration}
                    onConfirm={handlePostSubmit}
                    ProductInfos={ProductInfosContent}
                />
            )}
        </GlowModal.Container>
    );
};
